import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiChevronLeft, FiChevronRight, FiDownload, FiEdit3, FiTrash2 } from 'react-icons/fi';
import type { HREmployee, HRShift } from './hrData';
import { usDate } from './licenseUtils';
import TimeClockCard, { hoursLabel, isoTime, workedMinutes } from './TimeClockCard';
import { deleteShift, getTimesheet, localDateKey, updateShift, weekStartKey } from '../../services/hrService';

interface Props {
  me: HREmployee;
  /** Admins see (and can correct) everyone's shifts; everyone else sees their own. */
  admin: boolean;
  /** For the payroll export's email column. */
  employees: HREmployee[];
}

const OVERTIME_MIN = 40 * 60;

/** yyyy-MM-dd → the Date at local midnight. */
const parseDateKey = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (key: string, days: number) => {
  const d = parseDateKey(key);
  d.setDate(d.getDate() + days);
  return localDateKey(d);
};

/** An ISO timestamp as a datetime-local input value (browser time). */
const toInputValue = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${localDateKey(d)}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const mealLabel = (s: HRShift) => {
  if (s.mealBreakTaken === undefined || s.mealBreakTaken === null) return '';
  return s.mealBreakTaken ? 'Yes' : `No${s.mealBreakNote ? ` — ${s.mealBreakNote}` : ''}`;
};

/** Payroll CSV, one row per shift, for import into any payroll provider. */
const timesheetCsv = (shifts: HRShift[], employees: HREmployee[], now: Date) => {
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const rows = [['Employee', 'Email', 'Date', 'Clock in', 'Clock out', 'Break minutes', 'Hours worked', 'Corrected by admin', 'Meal break taken']];
  [...shifts]
    .sort((a, b) => a.employeeName.localeCompare(b.employeeName) || a.clockInAt.localeCompare(b.clockInAt))
    .forEach(s => {
      rows.push([
        s.employeeName,
        employees.find(e => e.id === s.employeeId)?.email ?? '',
        usDate(s.workDate),
        isoTime(s.clockInAt),
        s.clockOutAt ? isoTime(s.clockOutAt) : '',
        String(Math.round(s.breakMinutes)),
        (workedMinutes(s, now) / 60).toFixed(2),
        s.edited ? 'Yes' : 'No',
        mealLabel(s),
      ]);
    });
  return rows.map(r => r.map(esc).join(',')).join('\n');
};

const HRClock: React.FC<Props> = ({ me, admin, employees }) => {
  const thisWeek = weekStartKey();
  const [weekStart, setWeekStart] = useState(thisWeek);
  const [shifts, setShifts] = useState<HRShift[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editIn, setEditIn] = useState('');
  const [editOut, setEditOut] = useState('');
  // Bumped after an admin correction so the clock card's totals follow.
  const [cardKey, setCardKey] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    try {
      setShifts(await getTimesheet(weekStart));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the timesheet.');
    }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  /** Runs one change, then reloads the timesheet. Resolves false (and shows the error) if it failed. */
  const run = async (action: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
      setNow(new Date());
      setCardKey(k => k + 1);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const weekEnd = addDays(weekStart, 6);
  const inWeek = (s: HRShift) => s.workDate >= weekStart && s.workDate <= weekEnd;

  const myShifts = useMemo(() => (shifts ?? []).filter(s => s.employeeId === me.id), [shifts, me.id]);
  const myWeekMinutes = myShifts.filter(inWeek).reduce((n, s) => n + workedMinutes(s, now), 0);

  /** Admin: one group per person, people currently clocked in first. */
  const groups = useMemo(() => {
    const byEmployee = new Map<number, { name: string; shifts: HRShift[] }>();
    (shifts ?? []).forEach(s => {
      const g = byEmployee.get(s.employeeId) ?? { name: s.employeeName, shifts: [] };
      g.shifts.push(s);
      byEmployee.set(s.employeeId, g);
    });
    return Array.from(byEmployee.entries())
      .map(([employeeId, g]) => ({ employeeId, ...g, open: g.shifts.find(s => !s.clockOutAt) }))
      .sort((a, b) => Number(!!b.open) - Number(!!a.open) || a.name.localeCompare(b.name));
  }, [shifts]);

  const startEdit = (s: HRShift) => {
    setEditingId(s.id);
    setEditIn(toInputValue(s.clockInAt));
    setEditOut(toInputValue(s.clockOutAt));
  };

  const saveEdit = async (id: number) => {
    if (!editIn) return;
    const saved = await run(() => updateShift(
      id,
      new Date(editIn).toISOString(),
      editOut ? new Date(editOut).toISOString() : null,
    ));
    if (saved) setEditingId(null);
  };

  const weekShifts = (shifts ?? []).filter(inWeek);

  const exportCsv = () => {
    const blob = new Blob([timesheetCsv(weekShifts, employees, now)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timesheets-week-of-${usDate(weekStart).replace(/\//g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderShift = (s: HRShift) => (
    <li key={s.id} className="hr-to__row hr-clock__row">
      {editingId === s.id ? (
        <span className="hr-clock__edit">
          <label className="hr-to__field">
            Clock in
            <input type="datetime-local" value={editIn} onChange={e => setEditIn(e.target.value)} />
          </label>
          <label className="hr-to__field">
            Clock out
            <input type="datetime-local" value={editOut} min={editIn || undefined} onChange={e => setEditOut(e.target.value)} />
          </label>
          <span className="hr-dir__edit-actions">
            <button type="button" className="hr-dir__allocate-save" disabled={busy || !editIn} onClick={() => saveEdit(s.id)}>Save</button>
            <button type="button" className="hr-dir__allocate-cancel" onClick={() => setEditingId(null)}>Cancel</button>
          </span>
        </span>
      ) : (
        <>
          <span className="hr-to__row-main">
            <span>
              <strong>{parseDateKey(s.workDate).toLocaleDateString('en-US', { weekday: 'short' })} {usDate(s.workDate)}</strong>
              {' · '}{isoTime(s.clockInAt)} – {s.clockOutAt ? isoTime(s.clockOutAt) : <em>now</em>}
            </span>
            <span className="hr-to__row-comment">
              {s.breakMinutes > 0 || s.breakStartedAt ? `Breaks ${Math.round(s.breakMinutes)} min${s.breakStartedAt ? ' + on break now' : ''}` : 'No breaks'}
              {s.mealBreakTaken === false && ` · missed meal break${s.mealBreakNote ? ` (${s.mealBreakNote})` : ''}`}
              {s.edited && ' · corrected by admin'}
            </span>
          </span>
          <span className="hr-to__actions">
            <span className="hr-clock__hrs">{hoursLabel(workedMinutes(s, now))}</span>
            {admin && (
              <>
                <button type="button" className="hr-to__cancel" disabled={busy} onClick={() => startEdit(s)} aria-label="Edit shift">
                  <FiEdit3 size={11} aria-hidden="true" /> Edit
                </button>
                <button type="button" className="hr-settings__delete" disabled={busy} onClick={() => run(() => deleteShift(s.id))} aria-label="Delete shift">
                  <FiTrash2 size={12} />
                </button>
              </>
            )}
          </span>
        </>
      )}
    </li>
  );

  /** Admin table (same as vibe's Timesheets): one row per person for the week. */
  const tableRows = employees.map(e => {
    const list = weekShifts.filter(s => s.employeeId === e.id);
    return {
      e,
      count: list.length,
      minutes: list.reduce((n, s) => n + workedMinutes(s, now), 0),
      missedMeals: list.filter(s => s.mealBreakTaken === false).length,
      open: (shifts ?? []).some(s => s.employeeId === e.id && !s.clockOutAt),
    };
  });

  const weekNav = (
    <div className="hr-ts__week">
      <button type="button" className="hr-ts__icon" onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Previous week"><FiChevronLeft size={14} /></button>
      <span>{usDate(weekStart)} – {usDate(weekEnd)}</span>
      <button type="button" className="hr-ts__icon" disabled={weekStart >= thisWeek} onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Next week">
        <FiChevronRight size={14} />
      </button>
    </div>
  );

  return (
    <>
      {error && (
        <div className="hr__alert hr-clock__alert" role="alert">
          <span>{error}</span>
          <button type="button" className="hr__alert-action" onClick={() => (shifts ? setError(null) : load())}>
            {shifts ? 'Dismiss' : 'Retry'}
          </button>
        </div>
      )}

      <div className="hr-clock-tab">
        <TimeClockCard me={me} refreshKey={cardKey} onChanged={load} />

        <div className="hr-clock__side">
          {admin && (
            <section className="hr-ts glass-surface">
              <div className="hr-ts__head">
                <h3 className="hr-ts__title">Timesheets</h3>
                {weekNav}
                <button type="button" className="hr-ts__export" onClick={exportCsv} disabled={weekShifts.length === 0}>
                  <FiDownload size={13} aria-hidden="true" />Export CSV for payroll
                </button>
              </div>

              <div className="hr-ts__table" role="table" aria-label="Hours this week">
                <div className="hr-ts__row hr-ts__row--head" role="row">
                  <span role="columnheader">Employee</span>
                  <span role="columnheader">Pay type</span>
                  <span role="columnheader">Shifts</span>
                  <span role="columnheader">Hours</span>
                  <span role="columnheader">Flags</span>
                </div>
                {!shifts ? (
                  <p className="hr-dash__empty">Loading timesheet…</p>
                ) : tableRows.map(({ e, count, minutes, missedMeals, open }) => (
                  <div key={e.id} className="hr-ts__row" role="row">
                    <span role="cell" className="hr-ts__name">{e.name}</span>
                    {/* No exempt flag in the HR API yet, so everyone counts as hourly. [NEEDS VERIFICATION — backend] */}
                    <span role="cell"><span className="hr-ts__pay">Hourly</span></span>
                    <span role="cell">{count}</span>
                    <span role="cell" className="hr-ts__hours">{hoursLabel(minutes)}</span>
                    <span role="cell" className="hr-ts__flags">
                      {minutes > OVERTIME_MIN && <span className="hr-ts__flag hr-ts__flag--bad"><FiAlertTriangle size={10} aria-hidden="true" />{hoursLabel(minutes - OVERTIME_MIN)} overtime</span>}
                      {missedMeals > 0 && <span className="hr-ts__flag">{missedMeals} missed meal break{missedMeals === 1 ? '' : 's'}</span>}
                      {open && <span className="hr-ts__flag">Still clocked in</span>}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="hr-to__history hr-clock__sheet">
            <div className="hr-clock__sheet-head">
              <h3 className="hr-to__title">{admin ? 'Shifts (edit to correct)' : 'Your timesheet'}</h3>
              {!admin && weekNav}
            </div>
            {!admin && <div className="hr-clock__week">Your hours that week: <strong>{hoursLabel(myWeekMinutes)}</strong></div>}

            {!shifts ? (
              <p className="hr-dash__empty">Loading timesheet…</p>
            ) : shifts.length === 0 ? (
              <p className="hr-dash__empty">No shifts this week.</p>
            ) : admin ? (
              groups.map(g => (
                <div key={g.employeeId} className="hr-clock__group">
                  <div className="hr-clock__group-head">
                    <strong>{g.name}</strong>
                    {g.open && (
                      <span className={`hr-to__status ${g.open.breakStartedAt ? 'hr-to__status--pending' : 'hr-to__status--approved'}`}>
                        {g.open.breakStartedAt ? 'On break' : 'Clocked in'}
                      </span>
                    )}
                    <span className="hr-clock__hrs">{hoursLabel(g.shifts.filter(inWeek).reduce((n, s) => n + workedMinutes(s, now), 0))}</span>
                  </div>
                  <ul className="hr-to__list">{g.shifts.map(renderShift)}</ul>
                </div>
              ))
            ) : (
              <ul className="hr-to__list">{myShifts.map(renderShift)}</ul>
            )}
          </section>
        </div>
      </div>
    </>
  );
};

export default HRClock;
